import { FormEvent, useEffect, useMemo, useState } from 'react'

import {
  FieldType,
  FormField,
  FormModelStructure,
  FormModelStructureInput,
  FormModelValidationMode,
  LocalFile,
} from '@types'

import { useWindowSize } from '@utils/hooks'

// eslint-disable-next-line max-len
import { transformFormModelStructureToInput } from '../../../../project-form-models/components/FormBuilder/FormBuilder.helper'

interface UseFormSubmitterProps {
  formModel: FormModelStructure
  mode: 'edit' | 'preview'
  onFirstInteraction?: () => void
  onChange: (formModel: FormModelStructureInput, localFiles: Record<string, LocalFile[]>) => void
  onSubmit?: (formModel: FormModelStructureInput, localFiles: Record<string, LocalFile[]>) => void
}

interface FormSubmitterHook {
  fields: FormField[]
  hasSubmit: boolean
  isDisabled: boolean
  filteredFields: FormField[]
  showSubmitButton: boolean
  localFiles: Record<string, LocalFile[]>
  handleSubmit: (e: FormEvent<HTMLFormElement>) => void
  handleLocalFilesChange: (fieldId: string, files: LocalFile[]) => void
  handleFieldChange: (fieldId: string, updates: Partial<FormField>) => void
}

export const useFormSubmitter = ({
  formModel,
  mode,
  onFirstInteraction,
  onChange,
  onSubmit,
}: UseFormSubmitterProps): FormSubmitterHook => {
  const [hasSubmit, setHasSubmit] = useState<boolean>(false)
  const [hasInteracted, setHasInteracted] = useState<boolean>(false)
  const [fields, setFields] = useState<FormField[]>(formModel.fields)
  const [localFiles, setLocalFiles] = useState<Record<string, LocalFile[]>>({})
  const { isMobile } = useWindowSize()

  useEffect(() => {
    if (!hasInteracted) {
      // Skip the first render to avoid calling onChange with the initial values
      return
    }

    const data = { ...transformFormModelStructureToInput(fields, formModel.validationConfig) }

    onChange(data, localFiles)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields, hasInteracted, localFiles])

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    e.stopPropagation()

    const data = { ...transformFormModelStructureToInput(fields, formModel.validationConfig) }

    onSubmit?.(data, localFiles)
    setHasSubmit(true)
  }

  const handleLocalFilesChange = (fieldId: string, files: LocalFile[]) => {
    setLocalFiles((prev) => ({
      ...prev,
      [fieldId]: files,
    }))
  }

  const handleFieldChange = (fieldId: string, updates: Partial<FormField>) => {
    if (!hasInteracted && onFirstInteraction) {
      setHasInteracted(true)
      onFirstInteraction()
    }

    setFields(
      (prevFields) =>
        prevFields.map((field) => (field.id === fieldId ? { ...field, ...updates } : field)) as FormField[],
    )
  }

  const isDisabled = mode === 'preview'

  const filteredFields = useMemo(() => {
    return [
      ...fields.filter((field) => field.type === FieldType.HeaderPicture),
      ...fields.filter((field) => field.type === FieldType.Title),
      ...fields.filter((field) => field.type !== FieldType.Title && field.type !== FieldType.HeaderPicture),
    ]
  }, [fields])

  const showSubmitButton =
    mode === 'edit' && formModel.validationConfig.mode === FormModelValidationMode.Submit && !isMobile

  return {
    fields,
    hasSubmit,
    isDisabled,
    filteredFields,
    showSubmitButton,
    localFiles,
    handleSubmit,
    handleLocalFilesChange,
    handleFieldChange,
  }
}
